"use client";

import React from "react";
import {SingleProductEnum} from "@/constants/commerce-enum";
import {Button} from "@/components/ui/button";
import {Textarea} from "@/components/ui/textarea";
import {CircleX, PlusIcon, Trash2} from "lucide-react";
import Image from "next/image";
import {Checkbox} from "@/components/ui/checkbox";
import {Label} from "@/components/ui/label";
import {CommentGuidelines} from "@/components/ui/add-new-comment/comment-guidelines";
import {CommentRates} from "@/components/ui/add-new-comment/comment-rates";
import {CommentRate} from "@/components/ui/add-new-comment/comment-rates-data";
import MediumButton from "@/components/global/buttons/medium-button";

interface CommentFormValues {
    content: string;
    images: FileList;
    is_private: boolean;
}

interface AddNewCommentDesktopProps {
    register: any;
    commentRates: any[]
    selectedOption: CommentRate
    setSelectedOption: (option: CommentRate) => void
    viewImage: string[];
    inputFileRef: React.RefObject<HTMLInputElement>;
    handleFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    handleClick: () => void;
    deleteHandler: (index: number) => void;
    setValue: any;
    handleSubmit: any;
    onSubmit: (data: CommentFormValues) => void;
}

export function AddNewCommentDesktop({
                                        register,
                                        commentRates,
                                        setSelectedOption,
                                        selectedOption,
                                        viewImage,
                                        inputFileRef,
                                        handleFileChange,
                                        handleClick,
                                        deleteHandler,
                                        setValue,
                                        handleSubmit,
                                        onSubmit,
                                    }: AddNewCommentDesktopProps) {
    return (
        <section className="w-full flex flex-wrap text-xs md:text-base content-start justify-between">
            <div className="w-full md:w-1/2 p-3 md:p-5 py-3 border-e">
                {/* Rating */}
                <CommentRates commentRates={commentRates} selectedOption={selectedOption}
                             setSelectedOption={setSelectedOption}/>

                {/* Textarea */}
                <div className="w-full text-black flex flex-wrap mt-4 pb-3 border-b text-sm">
                    <div className="w-full py-2">
                        <div className="w-full flex text-xs">{SingleProductEnum.commentContent}</div>
                        <Textarea
                            {...register("content", {required: true})}
                            rows={5}
                            className="rounded-xl border border-gray-300 my-2"
                            dir="rtl"
                            placeholder=""
                        />
                    </div>
                </div>

                {/* Image upload */}
                <div className="w-full flex-wrap mt-4 pb-3">
                    <div className="text-sm w-full text-start mb-3">{SingleProductEnum.sendContent}</div>
                    <div className="w-full flex text-red-600 items-center justify-start mb-3 text-xs">
                        <CircleX className="w-4 h-4 me-1"/>
                        حداکثر ۳ عکس و هر کدام ۱ مگابایت با فرمت Jpeg
                    </div>

                    <div className="w-full flex flex-wrap mb-2 gap-3 mt-5">
                        {/* Add image button */}
                        <div
                            className="w-[120px] h-[120px] flex flex-wrap content-center items-center !border-2 rounded-xl !border-dotted border-gray-300">
                            <div
                                className="w-full flex hover:text-hover-blue-secondary text-blue-secondary justify-center items-center hover:text-text">
                                {SingleProductEnum.add}
                            </div>
                            <div className="relative w-full flex mt-5 items-center justify-center">
                                <input
                                    disabled={viewImage.length > 2}
                                    {...register("images")}
                                    multiple
                                    ref={inputFileRef}
                                    className="hidden"
                                    type="file"
                                    onChange={handleFileChange}
                                />
                                <Button
                                    variant="none"
                                    type="button"
                                    className="absolute bg-white top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
                                    onClick={handleClick}
                                >
                                    <PlusIcon
                                        className="w-4 h-4 text-orange-primary border rounded-full border-orange-primary"/>
                                </Button>
                            </div>
                        </div>

                        {/* Show uploaded images */}
                        {viewImage.map((item, index) => (
                            <div key={index} className="relative">
                                <Trash2
                                    onClick={() => deleteHandler(index)}
                                    className="absolute top-2 left-2 p-1 bg-gray-500 text-white rounded-lg"
                                />
                                <Image
                                    src={item || "/placeholder.svg"}
                                    alt=""
                                    className="rounded-xl my-1 object-cover w-[120px] h-[120px]"
                                    width={90}
                                    height={90}
                                />
                            </div>
                        ))}
                    </div>

                    {/* Checkbox */}
                    <div className="w-full flex flex-wrap">
                        <div className="w-full flex flex-wrap gap-1 text-blue-secondary mt-5 items-center text-sm">
                            <Checkbox onCheckedChange={(checked) => setValue("is_private", !!checked)} id="is_private"/>
                            <Label htmlFor="is_private">{SingleProductEnum.submitACommentAnonymously}</Label>
                        </div>
                    </div>
                </div>
            </div>

            {/* Text Guidelines */}
            <div className="w-full md:w-1/2 p-3 md:py-5 py-3 flex flex-col justify-between">
                <CommentGuidelines/>
                <div className="w-full flex mt-4">
                    <Button 
                        onClick={handleSubmit(onSubmit)}
                        className="w-full bg-orange-primary py-5 h-14"
                    >
                        ثبت نظر
                    </Button>
                </div>
            </div>
        </section>
    );
}